import { useState, useMemo, useCallback, ChangeEvent, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import * as yup from "yup";
import { getFundOptions } from "..";
import { useAppSelector } from "../../../../../store/hooks";
import {
  CartItems,
  EXISTING,
  InvestorFundDetails,
  NEW,
  NO,
  Options,
  SwitchInOptions,
  YES,
} from "../../../../../store/types";
import {
  convertToInr,
  getAdditionalPurchaseSchemesFromFolio as getSchemesFromFolio,
  getFolioOptions,
  getSelectedCartItem,
  getSchemeDetails,
  handleError,
} from "../../../../../utils/cart";
import {
  addItemsToCart,
  getAdditionalPurchaseSchemes,
  getSipCategory,
  updateCartItem,
} from "../../../../../store/actions";

interface LocationState {
  cartId: string;
  cartItemId: string | number;
  amc: string;
}

const FIELD_NAMES: Array<keyof Values> = [
  "amc",
  "folio",
  "purchaseType",
  "category",
  "scheme",
  "amount",
  "minAmount",
];

export const PURCHASE_TYPE_OPTIONS = [
  { label: "Existing Scheme", value: EXISTING },
  { label: "New Scheme", value: NEW },
];

export default function useAdditionalPurchase() {
  const dispatch = useDispatch();
  const location = useLocation<LocationState>();
  const history = useHistory();

  const { fundDetails } = useAppSelector((state) => state.cartReducer);

  //@ts-ignore
  const cartItems: CartItems<"APL"> = useAppSelector(
    (state) => state.cartReducer.cartItems
  );

  const { payload } = getSelectedCartItem<"APL">(cartItems, location.state);

  const isEdit = location.pathname.includes("edit");

  const [initialValues, setInitialValues] = useState<Values>({
    amc: payload.fund,
    amount: "",
    category: "",
    folio: "",
    purchaseType: "",
    scheme: "",
    minAmount: "",
  });

  const folioOptions = useMemo(
    () => getFolioOptions(fundDetails, payload.fund),
    [fundDetails, payload]
  );

  const [categoryOptions, setCategoryOptions] = useState<Options>([]);
  const [schemeOptions, setSchemeOptions] = useState<
    SwitchInOptions | ReturnType<typeof getSchemesFromFolio>
  >([]);

  const fundOptions = useMemo(() => getFundOptions(fundDetails), [fundDetails]);

  const resetLaterFields = useCallback(function (
    values: Values,
    key: keyof Values,
    fieldValues: Partial<Values>
  ): Values {
    const index = FIELD_NAMES.findIndex((fieldName) => fieldName === key);
    const updatedValues = FIELD_NAMES.slice(index).reduce<Partial<Values>>(
      (acc, curr) => {
        return { ...acc, [curr]: "" };
      },
      {}
    );

    return { ...values, ...updatedValues, ...fieldValues };
  },
  []);

  const handleFolioChange: HandleChange = function (values, setValues) {
    return async function (e) {
      setSchemeOptions(
        getSchemesFromFolio(values.amc, e.target.value, fundDetails)
      );

      setValues(
        resetLaterFields(values, "folio", {
          folio: e.target.value,
          purchaseType: EXISTING,
        })
      );
    };
  };

  function handleTypeChange(
    values: Values,
    setValues: React.Dispatch<React.SetStateAction<Values>>
  ) {
    return async function (e: any) {
      if (!e) return;
      if (e === EXISTING) {
        const schemes = getSchemesFromFolio(
          values.amc,
          values.folio,
          fundDetails
        );
        setSchemeOptions(schemes);
      }
      if (e === NEW) {
        const response = await getSipCategory(
          values.amc,
          values.folio
        )(dispatch);
        setCategoryOptions(response);
      }
      setValues(
        resetLaterFields(values, "purchaseType", {
          purchaseType: e,
        })
      );
    };
  }

  const handleCategoryChange: HandleChange = function (values, setValues) {
    return async function (e) {
      const response = await getAdditionalPurchaseSchemes({
        folio: values.folio,
        category: e.target.value,
        fundCode: values.amc,
      })(dispatch);
      setSchemeOptions(response);
      setValues(
        resetLaterFields(values, "category", {
          category: e.target.value,
        })
      );
    };
  };

  const handleSchemeChange: HandleChange = function handleSchemeChange(
    values,
    setValues
  ) {
    return async function (e) {
      // @ts-ignore
      const selectedScheme = schemeOptions.find(
        (scheme: any) => scheme.value === e.target.value
      );
      if ("MinAmt" in selectedScheme) {
        setValues(
          resetLaterFields(values, "scheme", {
            scheme: e.target.value,
            minAmount: selectedScheme.MinAmt.toString(),
          })
        );
      }
      if ("fm_newpur" in selectedScheme) {
        setValues(
          resetLaterFields(values, "scheme", {
            scheme: e.target.value,
            minAmount: selectedScheme.fm_newpur,
          })
        );
      }
    };
  };

  const handleComboSwitch: HandleCheckBoxChange = function (values, setValues) {
    return function (e, checked) {
      if (!checked) {
        setValues({ ...values, combo: false });
        return;
      }
      setValues({
        ...values,
        combo: window.confirm(
          "You have opted to do a Purchase cum Switch. Do you want to proceed?"
        ),
      });
    };
  };

  async function onSubmit(values: Values) {
    try {
      // @ts-ignore
      const selectedScheme = schemeOptions.find(
        (scheme: any) => scheme.value === values.scheme
      );
      const { scheme, plan, option } = getSchemeDetails(selectedScheme);
      const { Mobile: mobile, email } = fundDetails.find(
        ({ Acno, Fund, PAN }) =>
          values.amc === Fund &&
          values.folio.toString() === Acno.toString() &&
          payload.pan === PAN
      );
      let requestBody: any = {
        ...payload,
        scheme,
        plan,
        option,
        mobile,
        email,
        type: values.purchaseType,
        folio: values.folio,
        category: values.category,
        schemeName: selectedScheme.label,
        amount: values.amount,
        purchaseType: values.purchaseType,
        combo: values.combo,
      };
      if (selectedScheme && selectedScheme.combo_flag) {
        requestBody = { ...requestBody, combo: values.combo };
      }

      if (isEdit) {
        await updateTransaction(requestBody);
        return;
      }
      await addToCart(requestBody);
    } catch (error) {
      handleError(error);
    }
  }

  const updateTransaction = async function (requestBody: any) {
    try {
      const response = await updateCartItem({
        ...requestBody,
        cartId: location.state.cartId,
        cartItemId: location.state.cartItemId,
      })(dispatch);
      if (response) {
        goToCart();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const addToCart = async function (requestBody: any) {
    try {
      const response = await addItemsToCart(requestBody)(dispatch);
      if (response) {
        goToCart();
      }
    } catch (error) {
      handleError(error);
    }
  };

  function goToCart() {
    history.replace("/cart/investor-cart", {
      pan: payload.pan,
    });
  }

  const prefillForm = useCallback(
    async function () {
      const {
        fund,
        folio,
        category,
        purchaseType,
        amount,
        scheme,
        plan,
        option,
      } = payload;

      if (purchaseType === EXISTING) {
        const newSchemeOptions = getSchemesFromFolio(
          fund,
          folio.toString(),
          fundDetails
        );
        setSchemeOptions(newSchemeOptions);
        const selectedScheme = newSchemeOptions.find(
          (s) => s.Sch === scheme && s.Pln === plan && s.Opt === option
        );
        setInitialValues({
          amc: fund,
          amount: amount,
          purchaseType,
          folio: folio.toString(),
          scheme: selectedScheme.value,
          minAmount: selectedScheme.MinAmt.toString(),
          category: "",
        });
        return;
      }

      const categoriesResponse = await getSipCategory(
        fund,
        folio.toString()
      )(dispatch);

      const schemesResponse = await getAdditionalPurchaseSchemes({
        folio: folio,
        category: category,
        fundCode: fund,
      })(dispatch);

      const selectedScheme = schemesResponse.find(
        (opt) =>
          opt.fm_scheme === scheme &&
          opt.fm_plan === plan &&
          opt.fm_option === option
      );

      setCategoryOptions(categoriesResponse);
      setSchemeOptions(schemesResponse);

      setInitialValues({
        amc: fund,
        amount: amount,
        category: category,
        folio: folio.toString(),
        purchaseType: purchaseType,
        scheme: selectedScheme.value,
        minAmount: selectedScheme.fm_newpur,
      });
    },
    [payload, dispatch, fundDetails]
  );

  useEffect(() => {
    if (!location.pathname.includes("edit")) return;
    prefillForm();
  }, [location, prefillForm]);

  return {
    onSubmit,
    goToCart,
    options: {
      fundOptions,
      folioOptions,
      schemeOptions,
      categoryOptions,
    },
    changeHandlers: {
      handleFolioChange,
      handleTypeChange,
      handleCategoryChange,
      handleSchemeChange,
      handleComboSwitch,
    },
    validationSchema: validationSchema(
      fundDetails,
      payload.email,
      payload.mobile
    ),
    initialValues,
    isEdit,
    payload,
  };
}

export const validationSchema = (
  fundDetails: InvestorFundDetails[],
  email: string,
  mobile: string
) =>
  yup.object().shape({
    amc: yup.string().required("AMC is required"),
    folio: yup
      .string()
      .required("Folio is required")
      .test(
        "email",
        "Email of the selected folio does not match with email of other cart items",
        function (value) {
          const { amc } = this.parent;
          const selectedFolio = fundDetails.find(
            ({ Acno, Fund }) => Acno.toString() === value && amc === Fund
          );
          if (selectedFolio) {
            return selectedFolio.email === email;
          }
          return true;
        }
      )
      .test(
        "mobile",
        "Mobile Number of the selected folio does not match with mobile of other cart items",
        function (value) {
          const { amc } = this.parent;
          const selectedFolio = fundDetails.find(
            ({ Acno, Fund }) => Acno.toString() === value && amc === Fund
          );
          if (selectedFolio) {
            const { Mobile } = selectedFolio;
            return Mobile === mobile;
          }
          return true;
        }
      ),
    purchaseType: yup
      .string()
      .required("Scheme type is required")
      .oneOf([EXISTING, NEW]),
    category: yup.string().when("purchaseType", {
      is: NEW,
      then: (schema) => schema.required("Category is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    scheme: yup.string().required("Scheme is required"),
    amount: yup
      .string()
      .required("Amount is required")
      .test("maxAmountCheck", function (value) {
        if (+value > 10000000) {
          return this.createError({
            message: `Amount cannot exceed ${convertToInr(10000000)}`,
          });
        }
        return true;
      })
      .test("minAmountCheck", function (value) {
        const { minAmount } = this.parent;
        if (!minAmount) return true;
        if (Number(value) >= Number(minAmount)) return true;
        return this.createError({
          message: `Minimum amount (${convertToInr(minAmount)}) is required`,
        });
      }),
    minAmount: yup.string(),
  });

interface OnChange extends React.ChangeEvent<{ name?: string; value: any }> {}

type HandleChange = (
  values: Values,
  setValues: React.Dispatch<React.SetStateAction<Values>>
) => (event: OnChange) => any;

export interface Values {
  amc: string;
  amount: string;
  folio: string;
  category: string;
  purchaseType: string;
  scheme: string;
  minAmount?: string;
  combo?: boolean;
}

type HandleCheckBoxChange = (
  values: Values,
  setValues: React.Dispatch<React.SetStateAction<Values>>
) => (event: ChangeEvent<HTMLInputElement>, checked: boolean) => any;
